import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Fingerprint, CheckCircle2, XCircle, ShieldCheck, QrCode } from "lucide-react";

const StatusRow = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm font-medium text-foreground text-right" data-testid={testId}>{value}</p>
  </div>
);

export default function Verify() {
  const { declarationNumber } = useParams();
  const [number, setNumber] = useState("");
  const [hash, setHash] = useState("");
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!declarationNumber) return;
    setStatusLoading(true);
    api.get(`/public/declarations/${encodeURIComponent(declarationNumber)}/status`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ exists: false, detail: "Status lookup failed. Please try again." }))
      .finally(() => setStatusLoading(false));
  }, [declarationNumber]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/public/declarations/verify", {
        declaration_number: number.trim(),
        integrity_hash: hash.trim(),
      });
      setResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification request failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="verify-title">Verify a declaration</h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Verification confirms only that a matching, preserved record exists — it never reveals the voter's
          identity, contact details, address or signature.
        </p>
      </div>

      {declarationNumber && (
        <Card className="mt-8 max-w-lg" data-testid="verify-status-card">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <QrCode className="h-4 w-4 text-accent" />
              <h2 className="font-heading font-semibold text-base text-foreground">Receipt status</h2>
              <span className="font-mono text-xs text-muted-foreground ml-auto">{declarationNumber}</span>
            </div>
            {statusLoading && <Skeleton className="h-40" />}
            {status && !statusLoading && (
              <>
                <div
                  role="status"
                  aria-live="polite"
                  data-testid="verify-status-result"
                  className={`rounded-lg border p-4 flex items-start gap-3 ${
                    status.exists && status.record_intact
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                      : "bg-rose-50 border-rose-200 text-rose-900"
                  }`}
                >
                  {status.exists && status.record_intact
                    ? <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
                    : <XCircle className="h-5 w-5 mt-0.5 shrink-0" />}
                  <div>
                    <p className="font-semibold text-sm">
                      {status.exists
                        ? (status.record_intact ? "Record exists — integrity intact" : "Record exists — integrity problem")
                        : "No record found"}
                    </p>
                    <p className="text-xs mt-1 leading-relaxed">{status.detail}</p>
                  </div>
                </div>
                {status.exists && (
                  <div className="mt-4">
                    <StatusRow label="Declaration number" value={status.declaration_number} testId="status-number" />
                    {status.public_verification_id && (
                      <StatusRow label="Verification ID" value={status.public_verification_id} testId="status-verification-id" />
                    )}
                    <StatusRow label="Integrity status" value={status.integrity_status === "intact" ? "Intact (hash re-verified)" : "Integrity problem"} testId="status-integrity" />
                    <StatusRow
                      label="Witness level"
                      value={`${status.witness_level_label || "Self-Declared"}${status.witness_count ? ` (${status.witness_count} witness${status.witness_count > 1 ? "es" : ""})` : ""}`}
                      testId="status-witness-level"
                    />
                    <StatusRow label="Record locked" value={status.record_locked ? "Yes — locked after witnessing" : "No"} testId="status-locked" />
                    <StatusRow label="Declaration status" value={status.declaration_status} testId="status-decl" />
                    <StatusRow label="Election" value={`${status.election_label || "—"}${status.municipality_name ? ` · ${status.municipality_name}` : ""}${status.ward ? ` · ${status.ward}` : ""}`} testId="status-election" />
                    <StatusRow label="Signed (UTC)" value={status.submitted_at} testId="status-timestamp" />
                    {status.is_test && (
                      <div className="mt-3"><Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300" data-testid="status-test-badge">TEST DECLARATION</Badge></div>
                    )}
                    <p className="text-[11px] text-muted-foreground leading-relaxed mt-4 flex items-start gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-accent" /> {status.privacy_note}
                    </p>
                    {status.disclaimer && (
                      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2" data-testid="status-disclaimer">{status.disclaimer}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="mt-8 max-w-lg">
        <CardContent className="p-6 sm:p-8">
          <h2 className="font-heading font-semibold text-base text-foreground mb-1">Full integrity check</h2>
          <p className="text-xs text-muted-foreground mb-5">Enter the declaration number and SHA-256 hash from a receipt for a full number + hash match.</p>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="vnum">Declaration number</Label>
              <Input id="vnum" className="mt-1.5 font-mono" placeholder="WYV-2026-000123" value={number} onChange={(e) => setNumber(e.target.value)} data-testid="verify-input-number" />
            </div>
            <div>
              <Label htmlFor="vhash">SHA-256 integrity hash</Label>
              <Input id="vhash" className="mt-1.5 font-mono text-xs" placeholder="64-character hash from your receipt" value={hash} onChange={(e) => setHash(e.target.value)} data-testid="verify-input-hash" />
            </div>
            <Button type="submit" className="w-full h-11" disabled={!number.trim() || !hash.trim() || loading} data-testid="verify-form-submit-button">
              <Fingerprint className="h-4 w-4 mr-2" /> {loading ? "Verifying…" : "Verify"}
            </Button>
          </form>

          {result && (
            <div
              role="status"
              aria-live="polite"
              data-testid="verify-result"
              className={`mt-6 rounded-lg border p-4 flex items-start gap-3 ${
                result.valid ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"
              }`}
            >
              {result.valid ? <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" /> : <XCircle className="h-5 w-5 mt-0.5 shrink-0" />}
              <div>
                <p className="font-semibold text-sm">{result.valid ? "Verified" : "Not verified"}</p>
                <p className="text-xs mt-1 leading-relaxed">{result.detail}</p>
              </div>
            </div>
          )}

          {!declarationNumber && (
            <p className="text-[11px] text-muted-foreground mt-5">
              Tip: receipts include a QR code that opens this page with your receipt status pre-loaded.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
